import { useQuery } from '@tanstack/react-query';

import { getAuthMe } from '@/api/generated/auth/auth';
import type { GetAuthMe200User } from '@/api/generated/lucioAPI.schemas';
import { queryKeys } from '@/config/query/queryKeys';

const THIRTY_SECONDS = 30 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

async function fetchMe(): Promise<GetAuthMe200User> {
  const response = await getAuthMe();
  return response.user;
}

/**
 * Keeps the authenticated user's profile (including ragSearchEnabled and initialSyncCompletedAt)
 * fresh without requiring a logout/login cycle.
 *
 * - Refetches on window focus so re-opening the add-in always reflects current state.
 * - Polls every 30 minutes to keep the user profile fresh.
 * - Writes the result into queryKeys.auth.user() so all consumers stay in sync.
 */
export function useRefreshMe() {
  useQuery<GetAuthMe200User>({
    queryFn: fetchMe,
    queryKey: queryKeys.auth.user(),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.ragSearchEnabled && !data?.initialSyncCompletedAt) {
        return THIRTY_SECONDS;
      }
      return THIRTY_MINUTES;
    },
    refetchOnWindowFocus: true,
    staleTime: THIRTY_MINUTES,
  });
}
